let nuDropdownIdCounter = 0;

class NuDropdown extends HTMLElement {
    #select;
    #options = [];
    #selectedOption = null;
    #label;
    #required;
    #initialized = false;
    #instanceId = `nu-dropdown-${++nuDropdownIdCounter}`;
    #highlightedIndex = -1;
    #searchBuffer = '';
    #searchTimeout = null;
    #dropdownArrow = `<svg xmlns="http://www.w3.org/2000/svg" width="11.414" height="7.121" viewBox="0 0 11.414 7.121"><path id="Path_7" data-name="Path 7" d="M-16361.465,9642l5,5,5-5" transform="translate(16362.172 -9641.293)" fill="none" stroke="#000" stroke-width="2"/></svg>`;

    constructor() {
        super();
        if (this.querySelector('label')) {
            this.#build();
        }
    }

    #build() {
        if (this.#initialized) return;
        this.#initialized = true;

        this.#label = this.querySelector('label');
        if (!this.#label) {
            // Already-transformed HTML (e.g. restored from cache) — populate internal state
            // from the existing hidden <select> so selectItem() has options to work with.
            this.#select = this.querySelector('select');
            if (this.#select) {
                this.#options = Array.from(this.#select.options).map(option => ({
                    value: option.value,
                    text: option.textContent,
                    selected: option.selected
                }));
                this.#selectedOption = this.#options.find(opt => opt.selected) || this.#options[0];
            }
            return;
        }

        // Store custom attributes before replacing content
        const customAttributes = {};
        for (const attr of this.attributes) {
            customAttributes[attr.name] = attr.value;
        }

        this.#required = this.querySelector('span.required');

        // Handle both select and text input cases
        this.#select = this.querySelector('select');
        const textInput = this.querySelector('input[type="text"]');

        if (this.#select) {
            // Handle select dropdown
            this.#options = Array.from(this.#select.options).map(option => ({
                value: option.value,
                text: option.textContent,
                selected: option.selected
            }));
            this.#selectedOption = this.#options.find(opt => opt.selected) || this.#options[0];
        } else if (textInput) {
            // Handle text input (like date range)
            this.#options = [{
                value: '',
                text: textInput.value,
                selected: true
            }];
            this.#selectedOption = this.#options[0];
        }

        const labelText = this.#label.childNodes[0].textContent.trim();

        const hxTrigger = this.#select ? this.#select.getAttribute('hx-trigger') : '';

        // Create wrapper div with preserved attributes
        const wrapper = document.createElement('div');
        for (const [name, value] of Object.entries(customAttributes)) {
            wrapper.setAttribute(name, value);
        }

        wrapper.innerHTML = `
            <div class="dropdown">
                <button type="button" class="dropdown-button" aria-haspopup="listbox" aria-expanded="false">
                    <span class="dropdown-label">${labelText} ${this.#required ? '<span class="required">*</span>' : ''}</span>
                    <span class="dropdown-value">${this.#selectedOption?.text || 'Select'}</span>
                    ${this.#dropdownArrow}
                </button>
                ${this.#select ? `
                    <div class="dropdown-content" role="listbox">
                        ${this.#options.map((option, index) => `
                            <div class="dropdown-item" role="option" id="${this.#instanceId}-option-${index}" data-value="${option.value}" aria-selected="${option.selected ? 'true' : 'false'}">${option.text}</div>
                        `).join('')}
                    </div>
                    <select name="${this.#select.name}" ${hxTrigger ? `hx-trigger="${hxTrigger}"` : ''} hidden>
                        ${this.#select.innerHTML}
                    </select>
                ` : ''}
            </div>
        `;

        // Preserve any additional inputs (like hidden inputs for date range)
        const additionalInputs = Array.from(this.querySelectorAll('input[type="hidden"]'));
        additionalInputs.forEach(input => wrapper.appendChild(input.cloneNode(true)));

        // Replace content while preserving attributes
        this.replaceChildren(...wrapper.childNodes);

        this.#initialized = true;
    }

    #attachListeners() {
        const button = this.querySelector('.dropdown-button');

        button.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault(); // Prevent HTMX transition
            this.toggleDropdown();
        });

        button.addEventListener('keydown', (e) => this.#handleKeydown(e));

        this.querySelectorAll('.dropdown-item').forEach(item => {
            item.addEventListener('click', (e) => {
                e.stopPropagation();
                this.selectItem(e);
            });
        });

        // Click Outside Dropdown
        document.addEventListener('click', (e) => {
            if (!this.contains(e.target)) {
                this.closeDropdown();
            }
        });

        // Close when focus (keyboard or otherwise) leaves the component
        this.addEventListener('focusout', (e) => {
            if (!this.contains(e.relatedTarget)) {
                this.closeDropdown();
            }
        });

        // Prevent HTMX transition on form submit
        const form = this.closest('form');
        if (form) {
            form.addEventListener('submit', (e) => {
                if (this.querySelector('.dropdown').classList.contains('show')) {
                    e.stopPropagation();
                }
            });
        }
    }

    #handleKeydown(e) {
        const dropdown = this.querySelector('.dropdown');
        const isOpen = dropdown.classList.contains('show');

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                isOpen ? this.#moveHighlight(1) : this.openDropdown();
                break;
            case 'ArrowUp':
                e.preventDefault();
                isOpen ? this.#moveHighlight(-1) : this.openDropdown();
                break;
            case 'Home':
                if (isOpen) {
                    e.preventDefault();
                    this.#setHighlightedIndex(0);
                }
                break;
            case 'End':
                if (isOpen) {
                    e.preventDefault();
                    this.#setHighlightedIndex(this.#options.length - 1);
                }
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (isOpen) {
                    if (this.#highlightedIndex >= 0) {
                        this.#selectIndex(this.#highlightedIndex);
                    } else {
                        this.closeDropdown();
                    }
                } else {
                    this.openDropdown();
                }
                break;
            case 'Escape':
                if (isOpen) {
                    e.preventDefault();
                    this.closeDropdown();
                }
                break;
            case 'Tab':
                this.closeDropdown();
                break;
            default:
                if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                    this.#handleTypeahead(e.key);
                }
                break;
        }
    }

    #moveHighlight(direction) {
        const count = this.#options.length;
        if (!count) return;

        let index = this.#highlightedIndex;
        if (index === -1) {
            index = this.#options.findIndex(opt => opt.value === this.#selectedOption?.value);
        }
        index = (index + direction + count) % count;

        this.#setHighlightedIndex(index);
    }

    #setHighlightedIndex(index) {
        const items = this.querySelectorAll('.dropdown-item');
        if (!items.length) return;

        items.forEach(item => item.classList.remove('keyboard-highlighted'));

        this.#highlightedIndex = index;
        const item = items[index];
        if (item) {
            item.classList.add('keyboard-highlighted');
            item.scrollIntoView({ block: 'nearest' });
            this.querySelector('.dropdown-button').setAttribute('aria-activedescendant', item.id);
        }
    }

    #handleTypeahead(char) {
        const count = this.#options.length;
        if (!count) return;

        clearTimeout(this.#searchTimeout);
        this.#searchBuffer += char.toLowerCase();
        this.#searchTimeout = setTimeout(() => { this.#searchBuffer = ''; }, 500);

        const isOpen = this.querySelector('.dropdown').classList.contains('show');
        const startIndex = this.#highlightedIndex >= 0
            ? this.#highlightedIndex
            : this.#options.findIndex(opt => opt.value === this.#selectedOption?.value);

        for (let i = 1; i <= count; i++) {
            const index = (startIndex + i + count) % count;
            const option = this.#options[index];
            if (option.text.trim().toLowerCase().startsWith(this.#searchBuffer)) {
                if (isOpen) {
                    this.#setHighlightedIndex(index);
                } else {
                    this.#selectValue(option.value);
                }
                return;
            }
        }
    }

    #selectIndex(index) {
        const option = this.#options[index];
        if (option) {
            this.#selectValue(option.value);
        }
    }

    connectedCallback() {
        if (!this.#initialized) {
            this.#build();
        }
        this.#attachListeners();
    }

    closeDropdown() {
        this.querySelector('.dropdown').classList.remove('show');
        this.querySelector('.dropdown-button').setAttribute('aria-expanded', 'false');
        this.querySelectorAll('.dropdown-item').forEach(item => item.classList.remove('keyboard-highlighted'));
        this.#highlightedIndex = -1;
    }

    openDropdown() {
        // Close all other dropdowns first
        document.querySelectorAll('nu-dropdown .dropdown.show')
            .forEach(dropdown => {
                if (!this.contains(dropdown)) {
                    dropdown.classList.remove('show');
                }
            });

        this.querySelector('.dropdown').classList.add('show');
        this.querySelector('.dropdown-button').setAttribute('aria-expanded', 'true');

        const selectedIndex = this.#options.findIndex(opt => opt.value === this.#selectedOption?.value);
        this.#setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }

    toggleDropdown() {
        if (this.querySelector('.dropdown').classList.contains('show')) {
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    }

    selectItem(event) {
        const value = event.target.getAttribute('data-value');
        this.#selectValue(value);
    }

    #selectValue(value) {
        const optionEl = this.querySelector(`option[value="${value}"]`);
        if (!optionEl) return;

        optionEl.selected = true;
        this.#selectedOption = this.#options.find(opt => opt.value === value);

        this.querySelectorAll('.dropdown-item').forEach(item => {
            item.setAttribute('aria-selected', item.getAttribute('data-value') === value ? 'true' : 'false');
        });

        // Update the value span instead of button text
        this.querySelector('.dropdown-value').textContent = this.#selectedOption.text;

        // Dispatch change event to trigger HTMX
        const changeEvent = new Event('change', { bubbles: true });
        this.querySelector('select').dispatchEvent(changeEvent);

        const form = this.closest('form');

        if (form && form.classList.contains('htmx-enabled')) {
            form.dispatchEvent(changeEvent);
        }

        this.closeDropdown();
    }
}

customElements.define('nu-dropdown', NuDropdown);
